import { Injectable, NgZone } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

interface AttractionDetailsSheetState {
  isOpen: boolean;
  code: string | null;
}

/**
 * Drives the global attraction-details bottom sheet — the direct analogue of `ProductSheetService`,
 * keyed on the attraction's `provider_product_id` (a string) instead of a numeric product id.
 *
 * Hosted once in AppComponent above the router-outlet, so it overlays the `/attractions` list that
 * opened it (the list stays visible and dimmed behind it). Opening pushes a synthetic same-URL
 * history entry so browser Back closes the sheet; a programmatic navigation tears it down so the
 * overlay + `body.attr-sheet-open` scroll-lock never persist over the next screen.
 */
@Injectable({ providedIn: 'root' })
export class AttractionDetailsSheetService {
  private state = new BehaviorSubject<AttractionDetailsSheetState>({ isOpen: false, code: null });

  /** Emits on every open/close; AppComponent binds this with the async pipe. */
  readonly changes$: Observable<AttractionDetailsSheetState> = this.state.asObservable();

  /** True while our synthetic history entry is on the stack (so Back closes the sheet). */
  private hasHistoryEntry = false;

  constructor(private router: Router, private ngZone: NgZone) {
    // Browser Back: the browser has already popped our synthetic entry, so just close in place.
    window.addEventListener('popstate', this.onPopState);

    // Any programmatic route change while the sheet is open must close it; the router is taking over
    // history, so we must not pop our own entry here.
    this.router.events
      .pipe(filter(e => e instanceof NavigationStart))
      .subscribe(() => {
        if (this.isOpen) {
          this.hasHistoryEntry = false;
          this.reset();
        }
      });
  }

  get isOpen(): boolean {
    return this.state.value.isOpen;
  }

  get code(): string | null {
    return this.state.value.code;
  }

  /** Open the sheet over the current screen. */
  open(code: string): void {
    document.body.classList.add('attr-sheet-open');
    // Push a same-URL history entry once per open session so Back closes the sheet.
    if (!this.hasHistoryEntry) {
      history.pushState({ attrSheet: true }, '', location.href);
      this.hasHistoryEntry = true;
    }
    this.state.next({ isOpen: true, code });
  }

  /** User-initiated close (X / backdrop). Pops the synthetic history entry pushed on open. */
  close(): void {
    const hadEntry = this.hasHistoryEntry;
    this.reset();
    if (hadEntry) {
      history.back();
    }
  }

  private onPopState = (): void => {
    if (!this.isOpen) return;
    this.ngZone.run(() => {
      this.hasHistoryEntry = false;
      this.reset();
    });
  };

  /** Clear sheet state (and body scroll-lock) without touching history. */
  private reset(): void {
    this.hasHistoryEntry = false;
    document.body.classList.remove('attr-sheet-open');
    this.state.next({ isOpen: false, code: null });
  }
}
